import { getStoredSpecializationName } from '../services/hhDictionariesService';
import { extractLastPathSegment } from '../utils/filterUtils';
import { getSourceConfig } from '.';
import type { DictItem } from './types';

type SpecializationDisplayItem = DictItem & {
  roles?: SpecializationDisplayItem[];
  children?: SpecializationDisplayItem[];
};

const normalizeSourceId = (sourceId?: string): string => sourceId || 'hh';

export const getSpecializationDisplayCacheKey = (
  sourceId: string | undefined,
  specializationId: string | number,
): string => `${normalizeSourceId(sourceId)}:${String(specializationId).trim()}`;

const findSpecializationName = (
  items: SpecializationDisplayItem[],
  specializationId: string,
): string | null => {
  const normalizedId = String(specializationId).trim();
  const leafId = extractLastPathSegment(normalizedId);

  for (const item of items) {
    const itemId = String(item.id).trim();
    if (itemId === normalizedId || (leafId && itemId === leafId)) {
      return item.name;
    }

    const children = item.children || item.roles || [];
    if (children.length > 0) {
      const childName = findSpecializationName(children, normalizedId);
      if (childName) return childName;
    }
  }

  return null;
};

export const getSpecializationDisplayName = async (
  sourceId: string | undefined,
  specializationId: string | number,
): Promise<string> => {
  const normalizedSourceId = normalizeSourceId(sourceId);
  const rawId = String(specializationId).trim();
  if (!rawId) return '';

  if (normalizedSourceId === 'hh') {
    const name = await getStoredSpecializationName(rawId);
    return name && name !== rawId
      ? name
      : extractLastPathSegment(rawId) || rawId;
  }

  const specializationLoader = getSourceConfig(normalizedSourceId).dataLoaders?.specialization;
  if (!specializationLoader) return extractLastPathSegment(rawId) || rawId;

  const items = await specializationLoader();
  return findSpecializationName(items as SpecializationDisplayItem[], rawId)
    || extractLastPathSegment(rawId)
    || rawId;
};
